/**
 * Portfolio validation and business rules
 * Handles validation logic for portfolio operations
 */

import { useEffect } from 'react';

/**
 * Custom hook for portfolio validation and business rules
 * @param {Object} portfolioState - Current portfolio state
 * @param {Object} cleanupUtils - Cleanup utility functions
 * @returns {Object} Validation utilities
 */
const usePortfolioValidation = (portfolioState, cleanupUtils) => {
  const {
    pinnedClients,
    pinnedViewType,
    getClientKey
  } = portfolioState;

  const {
    cleanupOnViewChange,
    validateExpandedRow
  } = cleanupUtils;

  // Reset expanded states when view type changes
  useEffect(() => {
    cleanupOnViewChange();
  }, [pinnedViewType, cleanupOnViewChange]);

  // Validate expanded row when pinned clients change
  useEffect(() => {
    validateExpandedRow(pinnedClients);
  }, [pinnedClients, validateExpandedRow]);

  /**
   * Validate if a client can be pinned
   * @param {Object} client - Client to validate
   * @returns {boolean} True if client can be pinned
   */
  const canPinClient = (client) => {
    if (!client || !client.client_name || !client.country) {
      return false;
    }
    
    // Check if client is already pinned
    const clientKey = getClientKey(client);
    return !pinnedClients.some(pinned => getClientKey(pinned) === clientKey);
  };

  /**
   * Validate if a client can be unpinned
   * @param {Object} client - Client to validate
   * @returns {boolean} True if client can be unpinned
   */
  const canUnpinClient = (client) => {
    if (!client || !client.client_name || !client.country) {
      return false;
    }
    
    // Check if client is currently pinned
    const clientKey = getClientKey(client);
    return pinnedClients.some(pinned => getClientKey(pinned) === clientKey);
  };

  /**
   * Validate portfolio state consistency
   * @returns {Object} Validation results
   */
  const validatePortfolioState = () => {
    const issues = [];
    
    // Check for duplicate clients
    const clientKeys = new Set();
    const duplicates = [];
    
    pinnedClients.forEach(client => {
      const key = getClientKey(client);
      if (clientKeys.has(key)) {
        duplicates.push(key);
      } else {
        clientKeys.add(key);
      }
    });
    
    if (duplicates.length > 0) {
      issues.push(`Duplicate clients found: ${duplicates.join(', ')}`);
    }
    
    // Check for invalid client data
    const invalidClients = pinnedClients.filter(client => 
      !client.client_name || !client.country
    );
    
    if (invalidClients.length > 0) {
      issues.push(`Invalid client data found: ${invalidClients.length} clients`);
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  };

  return {
    canPinClient,
    canUnpinClient,
    validatePortfolioState
  };
};

export default usePortfolioValidation;
